import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Divider,
  Tooltip,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartIcon from "@mui/icons-material/TableChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CodeIcon from "@mui/icons-material/Code";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DynamicNestedTable from "../DynamicTable/DynamicNestedTable";
import { useSqlResults } from "../../contexts/SqlResultsContext";
import { useAsyncDownload } from "../../hooks/useAsyncDownload";
import { useAuth } from "../../hooks/useAuth";
import { StorageHelper } from "../../utils/storageHelper";

/** Format plan column name for display (e.g. parentOp -> Parent op, STEP -> Step) */
function formatPlanLabel(key) {
  if (!key || typeof key !== "string") return key;
  const lower = key.toLowerCase();
  const withSpaces = lower.replace(/_/g, " ");
  return withSpaces.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Extract estimated row count from EXPLAIN plan (e.g. from rowCount in Expressions or a rowCount column) */
function getRowCountFromPlan(planData) {
  if (!Array.isArray(planData) || planData.length === 0) return null;
  for (const row of planData) {
    const keys = Object.keys(row);
    for (const k of keys) {
      if (/rowcount|row_count/i.test(k)) {
        const v = row[k];
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string" && /^\d+$/.test(v.trim())) return parseInt(v.trim(), 10);
      }
    }
    const exprKey = keys.find((k) => /expression/i.test(k));
    if (exprKey) {
      const expr = row[exprKey];
      if (typeof expr === "string") {
        const m = expr.match(/rowCount\s*:\s*(\d+)/i);
        if (m) return parseInt(m[1], 10);
      }
    }
  }
  return null;
}

/** High-level overview + full execution plan (not table) for Verify SQL */
function VerifyOverview({ overview, planStepCount, planData = [], planColumns = [] }) {
  const { tables = [], joinCount = 0, selectColumnCount = 0 } = overview || {};
  const tableList = tables.length ? tables.join(", ") : "—";
  const keys = planColumns.length > 0 ? planColumns : (planData[0] ? Object.keys(planData[0]) : []);
  const rowCountFromPlan = getRowCountFromPlan(planData);
  const displayCount = rowCountFromPlan != null ? rowCountFromPlan : "—";

  return (
    <Box sx={{ flex: 1, overflow: "auto", p: 2, pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Compact overview: single card, grid of stats */}
      <Box
        sx={{
          flexShrink: 0,
          p: 1.5,
          borderRadius: 1,
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          minWidth: 0,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", mb: 1 }}>
          Overview
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, "@media (min-width: 360px)": { gridTemplateColumns: "3fr 1fr 1fr 1fr" }, minWidth: 0, gridAutoRows: "minmax(min-content, auto)" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.6875rem", display: "block" }}>Tables</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", fontSize: "0.8125rem" }}>{tables.length}</Typography>
            {tableList !== "—" && (
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.6875rem", display: "block", mt: 0.25, wordBreak: "break-word", overflowWrap: "anywhere" }} title={tableList}>
                {tableList}
              </Typography>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.6875rem", display: "block" }}>Columns</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", fontSize: "0.8125rem" }}>{selectColumnCount}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.6875rem", display: "block" }}>Joins</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", fontSize: "0.8125rem" }}>{joinCount}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.6875rem", display: "block" }}>Count</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", fontSize: "0.8125rem" }}>{displayCount}</Typography>
          </Box>
        </Box>
      </Box>

      {planData.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#334155", mb: 1.5, fontSize: "0.8125rem" }}>
            Execution plan (full)
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {planData.map((row, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", display: "block", mb: 1 }}>
                  Step {idx + 1}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {keys.map((key) => {
                    const val = row[key];
                    const display = val === null || val === undefined ? "—" : String(val);
                    return (
                      <Box key={key} sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "#64748b", fontWeight: 500, minWidth: 100, flexShrink: 0 }}
                        >
                          {formatPlanLabel(key)}:
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "#334155", wordBreak: "break-word" }}
                        >
                          {display}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const ResultsPanel = ({ tabName}) => {
  const { results, isPanelOpen, setIsPanelOpen, togglePanel } = useSqlResults();
  const { startAsyncDownload } = useAsyncDownload();
  const { user } = useAuth();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showSqlQuery, setShowSqlQuery] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Adjustable panel width (fraction of viewport width), persisted in localStorage
  const [panelWidth, setPanelWidth] = useState(() => {
    if (typeof window === "undefined") return 0.5;
    try {
      const stored = window.localStorage.getItem("results_panel_width");
      const val = stored ? parseFloat(stored) : NaN;
      if (!Number.isNaN(val) && val >= 0.3 && val <= 0.8) {
        return val;
      }
    } catch {
      // ignore
    }
    return 0.5; // default to 50% of viewport
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("results_panel_width", String(panelWidth));
    } catch {
      // ignore
    }
  }, [panelWidth]);

  const [isResizing, setIsResizing] = useState(false);

  // Helper function to truncate SQL query for preview
  const truncateSql = (sql, maxLength = 80) => {
    if (!sql) return "";
    if (sql.length <= maxLength) return sql;
    return sql.substring(0, maxLength) + "...";
  };

  // Parse SQL for high-level overview (tables, joins, select columns) - used for Verify result
  const parseSqlOverview = (sql) => {
    if (!sql || typeof sql !== "string") return { tables: [], joinCount: 0, selectColumnCount: 0 };
    const normalized = sql.replace(/\s+/g, " ").trim();
    const tables = [];
    const tableRegex = /(?:FROM|JOIN)\s+([A-Za-z0-9_.]+)(?:\s+(?:AS\s+)?[a-zA-Z0-9_]+)?/gi;
    let m;
    while ((m = tableRegex.exec(normalized)) !== null) {
      const name = m[1].trim();
      if (name && !tables.includes(name)) tables.push(name);
    }
    const joinCount = (normalized.match(/\bJOIN\b/gi) || []).length;
    let selectColumnCount = 0;
    const fromIdx = normalized.toUpperCase().indexOf(" FROM ");
    if (fromIdx > 0) {
      const selectPart = normalized.slice(0, fromIdx).replace(/^\s*SELECT\s+/i, "").trim();
      if (selectPart) selectColumnCount = (selectPart.match(/,/g) || []).length + 1;
    }
    return { tables, joinCount, selectColumnCount };
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (!results) {
    return null;
  }

  const isVerifyResult = results.source === "verify";

  const handleDownload = async () => {
    if (!results?.success || !results?.data || results.data.length === 0) {
      return;
    }

    if (!results?.sql || !results.sql.trim()) {
      return;
    }

    setDownloadLoading(true);
    try {
      const userId = user?.userDetails?.user_id;
      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      const userDbId = StorageHelper.get("selected_user_db_id");
      if (!userDbId) {
        throw new Error("Database connection not found. Please select a database.");
      }

      const db_type = "snowflake";
      const columns = results.columns || [];
      const rows = [];
      const data = columns;

      // Prefer originalQuery from MCP (unlimited) when available, fall back to sql otherwise.
      console.log("The results is", results);
      const rawSql = (results.originalQuery || results.sql || "").trim();
      if (!rawSql) {
        throw new Error("No SQL query found for download.");
      }
      const cleanedSql = rawSql.replace(/;+$/, "").trim();

      const effectiveTabName = (tabName && String(tabName).trim());
      const safeTabName = effectiveTabName.replace(/\s+/g, "_");

      const result = await startAsyncDownload(
        { sql: cleanedSql },
        Number(userId),
        Number(userDbId),
        columns,
        rows,
        data,
        db_type,
        "csv",
        "plain",
        safeTabName
      );

      if (result.success) {
        console.log(`✅ Async download started for job ${result.jobId}`);
        setSnackbar({
          open: true,
          message: "CSV download started. You will receive the file shortly.",
          severity: "success",
        });
      } else {
        console.error("❌ Async download failed:", result.error);
        setSnackbar({
          open: true,
          message: result.error || "Download failed. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Download error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Download failed. Please try again.",
        severity: "error",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    setIsResizing(true);

    const onMouseMove = (moveEvent) => {
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || 1;
      const deltaX = moveEvent.clientX - startX;

      // Panel is anchored to the right; compute new width from current left edge
      // Left edge ≈ viewportWidth - panelWidth * viewportWidth
      const leftEdge = viewportWidth * (1 - panelWidth) + deltaX;
      let newWidth = (viewportWidth - leftEdge) / viewportWidth;

      // Clamp between 30% and 80% of viewport
      newWidth = Math.max(0.3, Math.min(0.8, newWidth));
      setPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      setIsResizing(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const panelWidthVw = `${panelWidth * 100}vw`;

  return (
    <>
      {/* Collapse/Expand Button */}
      <Box
        sx={{
          position: "fixed",
          right: isPanelOpen ? panelWidthVw : "0",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1001,
          transition: isResizing ? "none" : "right 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Tooltip 
          title={isPanelOpen ? "Collapse Results" : "Show Results"}
          arrow
          placement="left"
        >
          <IconButton
            onClick={togglePanel}
            sx={{
              position: "absolute",
              right: 0,
              backgroundColor: "rgb(44, 52, 123)",
              color: "#fff",
              borderRadius: "8px 0 0 8px",
              width: 24,
              height: 48,
              minWidth: 24,
              padding: 0,
              boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
              "&:hover": {
                backgroundColor: "rgba(44, 52, 123, 0.8)",
              },
              visibility: "visible",
              opacity: 1,
            }}
          >
            {isPanelOpen ? (
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Results Panel */}
      <Box
        sx={{
          position: "fixed",
          right: isPanelOpen ? 0 : `calc(-${panelWidthVw} - 1px)`,
          top: 0,
          width: panelWidthVw,
          height: "100vh",
          backgroundColor: "#ffffff",
          boxShadow: "-2px 0 10px rgba(0, 0, 0, 0.1)",
          transition: isResizing ? "none" : "right 0.3s ease-in-out",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Drag handle for resizing width */}
        <Box
          sx={{
            position: "absolute",
            left: -4,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: "col-resize",
            zIndex: 1002,
          }}
          onMouseDown={handleResizeMouseDown}
        />
        {/* Header */}
        <Box
          sx={{
            padding: "12px 16px",
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TableChartIcon sx={{ fontSize: 18, color: "#3F49A7" }} />
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "#1e293b",
                }}
              >
                {isVerifyResult ? "Execution plan" : "Query Results"}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsPanelOpen(false)}
              sx={{
                color: "#64748b",
                "&:hover": { backgroundColor: "#f1f5f9" },
                padding: "4px",
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          
          {/* SQL Query Preview */}
          {results?.sql && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                border: "1px solid #e1e5e9",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
              }}
              onClick={() => setShowSqlQuery(!showSqlQuery)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <CodeIcon
                      sx={{ fontSize: 14, color: "#64748b", flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {isVerifyResult ? "Query (verified)" : "SQL Query"}
                    </Typography>
                    {results.timestamp && (
                      <>
                        <Typography
                          sx={{
                            fontSize: "0.6875rem",
                            color: "#94a3b8",
                            mx: 0.5,
                          }}
                        >
                          •
                        </Typography>
                        <AccessTimeIcon
                          sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.6875rem",
                            color: "#94a3b8",
                          }}
                        >
                          {formatTimestamp(results.timestamp)}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      fontFamily:
                        "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', monospace",
                      color: "#334155",
                      wordBreak: "break-word",
                      whiteSpace: showSqlQuery ? "pre-wrap" : "nowrap",
                      overflow: showSqlQuery ? "visible" : "hidden",
                      textOverflow: showSqlQuery ? "clip" : "ellipsis",
                    }}
                  >
                    {showSqlQuery ? results.sql : truncateSql(results.sql, 70)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  sx={{
                    padding: "2px",
                    color: "#64748b",
                    flexShrink: 0,
                  }}
                >
                  {showSqlQuery ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Status Section with Download Button */}
          <Box
            sx={{
              p: 1,
              px: 1.5,
              borderBottom: "1px solid #f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {results.success ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: "#22c55e", flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#22c55e",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isVerifyResult ? "Execution plan generated" : "Query executed successfully"}
                </Typography>
                {results.row_count !== undefined && (
                  <Chip
                    label={isVerifyResult ? `${results.row_count} step${results.row_count !== 1 ? "s" : ""}` : `${results.row_count} row${results.row_count !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.625rem",
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                      fontWeight: 500,
                      "& .MuiChip-label": {
                        px: 0.75,
                      },
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
                <ErrorIcon sx={{ fontSize: 14, color: "#ef4444", flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#ef4444",
                  }}
                >
                  Query failed
                </Typography>
              </Box>
            )}
            
            {/* Download Button - hide for Verify SQL (execution plan) */}
            {results.success && results.data && results.data.length > 0 && !isVerifyResult && (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  downloadLoading ? (
                    <CircularProgress size={12} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 12 }} />
                  )
                }
                onClick={handleDownload}
                disabled={downloadLoading}
                sx={{
                  borderColor: "#3F49A7",
                  color: "#3F49A7",
                  fontWeight: 500,
                  fontSize: "0.6875rem",
                  textTransform: "none",
                  borderRadius: 0.75,
                  px: 1,
                  py: 0.25,
                  minWidth: "auto",
                  height: 24,
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "#3F49A7",
                    backgroundColor: "rgba(63, 73, 167, 0.08)",
                  },
                  "&:disabled": {
                    borderColor: "#cbd5e1",
                    color: "#94a3b8",
                  },
                  "& .MuiButton-startIcon": {
                    marginRight: 0.5,
                    marginLeft: 0,
                  },
                }}
              >
                {downloadLoading ? "Downloading..." : "Download CSV"}
              </Button>
            )}
          </Box>

          {/* Data Section */}
          {results.success && results.data && results.data.length > 0 ? (
            <>
              {isVerifyResult ? (
                <VerifyOverview
                  overview={parseSqlOverview(results.sql)}
                  planStepCount={results.data?.length ?? results.row_count ?? 0}
                  planData={results.data || []}
                  planColumns={results.columns || []}
                />
              ) : (
                <Box sx={{ flex: 1, overflow: "auto", p: 2, pt: 1 }}>
                  <DynamicNestedTable
                    tableData={results.data || []}
                    tableLoading={false}
                    headers={results.columns || undefined}
                    computedColumns={[]}
                  />
                </Box>
              )}
            </>
          ) : results.success ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <TableChartIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  No data returned from query
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#ef4444",
                  fontSize: "0.8125rem",
                }}
              >
                {results.error || "Unknown error occurred"}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Success/Error Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            borderRadius: 2,
            backgroundColor: snackbar.severity === "success" ? "#f0fdf4" : undefined,
            color: snackbar.severity === "success" ? "#166534" : undefined,
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#22c55e" : undefined,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResultsPanel;

